import { Component, ElementRef, HostListener, Injector, afterNextRender, computed, effect, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DocumentService } from '../../../core/services/document.service';
import { BootstrapService } from '../../../core/services/bootstrap.service';
import { UserSessionService } from '../../../core/services/user-session.service';
import { AuthorizationService } from '../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../core/services/authorization/authorization-state.service';
import { DocumentsPermission } from '../../../core/enums/permissions.enums';
import { BulkActionResultModel, DocumentBulkActionRequest, DocumentNodeModel, DocumentStorageUsageModel, DocumentViewFilter, FileQueryCriteria, FolderBulkActionRequest, FolderModel } from '../../../core/models/data/document.model';
import { DocumentUploadProgressModel } from '../../../core/models/ui/document-view.model';
import { DocumentListSortOptions } from '../../../core/enums/sort-options.enums';
import { DocumentNodeType, DocumentPermission, DocumentStatus, DocumentViewLayout, DocumentViewMode } from '../../../core/enums/data.enums';
import { AuditEntityType, ModalActions, ModalTypes } from '../../../core/enums/base.enums';
import { LocalStorageKeyConstants } from '../../../core/constants/constants';
import { DocumentsConstants } from '../../../core/constants/documents.constants';
import { DocumentsMessagingConstants } from '../../../core/constants/messaging.constants';
import { DocumentUtilities } from '../../../core/utilities/document.utilities';
import { DocumentStatusUtilities } from '../../../core/utilities/document-status.utilities';
import { FileDownloadUtilities } from '../../../core/utilities/file-download.utilities';
import { TooltipDirective } from '../../../core/directives/tooltip.directive';
import { CanComponentDeactivate } from '../../../core/routing/can-deactivate.guard';

import { DocumentToolbarComponent } from '../components/document-toolbar/document-toolbar.component';
import { DocumentListComponent } from '../components/document-list/document-list.component';
import { DocumentStorageUsageComponent } from '../components/document-storage-usage/document-storage-usage.component';
import { DocumentUploadOverlayComponent } from '../components/document-upload-overlay/document-upload-overlay.component';
import { OrgDatePipe } from '../../../core/pipes/org-date/org-date.pipe';
import { DocumentPermissionsModalComponent, PermissionUpdateResult } from '../components/document-permissions-modal/document-permissions-modal.component';
import { DocumentRenameModalComponent, RenameMode } from '../components/document-rename-modal/document-rename-modal.component';
import { DocumentMoveFolderModalComponent } from '../components/document-move-folder-modal/document-move-folder-modal.component';
import { DocumentBulkMoveFilesModalComponent } from '../components/document-bulk-move-files-modal/document-bulk-move-files-modal.component';
import { DocumentUploadManagerService, UploadCompletedEvent } from '../services/document-upload-manager.service';
import { ConfirmationModalComponent } from '../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../parts/info-modal/info-modal.component';
import { AuditLogsComponent } from '../../../parts/audit-logs/audit-logs.component';

interface DocumentListItem {
    node: DocumentNodeModel;
}

@Component({
    selector: 'ms-documents-page',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        FontAwesomeModule,
        OrgDatePipe,
        DocumentToolbarComponent,
        DocumentListComponent,
        DocumentStorageUsageComponent,
        DocumentUploadOverlayComponent,
        DocumentPermissionsModalComponent,
        DocumentRenameModalComponent,
        DocumentMoveFolderModalComponent,
        DocumentBulkMoveFilesModalComponent,
        ConfirmationModalComponent,
        InfoModalComponent,
        AuditLogsComponent,
        TooltipDirective
    ],
    providers: [DocumentUploadManagerService],
    templateUrl: './documents.page.html',
    styleUrl: './documents.page.scss',
    host: { 'class': 'documents-page-host' }
})
export class DocumentsPage implements OnInit, OnDestroy, CanComponentDeactivate {

    private documentService = inject(DocumentService);
    private bootstrapService = inject(BootstrapService);
    private userSessionService = inject(UserSessionService);
    private authorizationService = inject(AuthorizationService);
    private authorizationStateService = inject(AuthorizationStateService);
    private injector = inject(Injector);
    private breakpointObserver = inject(BreakpointObserver);
    public uploadManager = inject(DocumentUploadManagerService);

    // Mobile uses the app's standard handset/tablet breakpoint. Drives the folder-nav buttons up from
    // btn-sm to the larger default btn size so they're an easier touch target on phones.
    public isMobile = signal<boolean>(false);

    public nodes = signal<DocumentNodeModel[]>([]);
    public folderTree = signal<DocumentNodeModel[]>([]);
    public currentFolderId = signal<number | undefined>(undefined);
    // Folders and files are separate backend entities with independent id sequences (FolderModel.folderId
    // vs DocumentModel.documentId) — a folder and a file can legitimately share the same documentNodeId.
    // Kept as two separate sets (rather than one shared Set<number>) so selecting one never bleeds into
    // the other just because their ids happen to collide (DEV-2572).
    public selectedFolderIds = signal<Set<number>>(new Set<number>());
    public selectedFileIds = signal<Set<number>>(new Set<number>());
    // "Select all files" true-select-all state (DEV-2481): while active, `selectedFileIds` is kept in
    // sync for visual feedback (and grows as more pages load), but the authoritative selection for the
    // bulk-delete-files request is "every file matching the current filter except `deselectedFileIds`".
    // Folders need no such mechanism — they're always eager-loaded in full, so their "select all" is
    // just every currently-loaded folder id.
    public isAllFilesSelected = signal<boolean>(false);
    public deselectedFileIds = signal<Set<number>>(new Set<number>());
    // Total files matching the current filter (from the file page's X-Pagination header), used to
    // compute the "Delete files (N)" count while select-all is active without loading every page.
    public filesTotalCount = signal<number>(0);
    public sort = signal<DocumentListSortOptions>(DocumentListSortOptions.NameAsc);
    // Independent per-table sort for List view only (DEV-2572) — Grid/Mobile keep using `sort` above,
    // completely unaffected. Each starts as a derived mirror of `sort` (clamped for folders, which have
    // no Size/Type) and only diverges into independent state once the user clicks that table's own
    // header, via `folderSortOverride`/`fileSortOverride`. The undiverged case costs nothing extra —
    // folders never need a fetch to re-sort (always eager-loaded in full) and files reuse the same
    // `nodes` data Grid/Mobile already have loaded. The toolbar dropdown (`onGlobalSortChanged`) resets
    // both overrides back to undefined, so it still meaningfully "does something" while in List view.
    private folderSortOverride = signal<DocumentListSortOptions | undefined>(undefined);
    public folderSort = computed<DocumentListSortOptions>(() =>
        this.clampSortToFolderContext(this.folderSortOverride() ?? this.sort())
    );
    private fileSortOverride = signal<DocumentListSortOptions | undefined>(undefined);
    public fileSort = computed<DocumentListSortOptions>(() => this.fileSortOverride() ?? this.sort());
    public isFileSortDiverged = computed<boolean>(() => this.fileSortOverride() != null);
    // Independent file data for List view once diverged — never read by Grid/Mobile.
    public divergedFileNodes = signal<DocumentNodeModel[]>([]);
    public divergedHasMoreFiles = signal<boolean>(false);
    public isFilesRefreshing = signal<boolean>(false);
    public isLoadingMoreDivergedFiles = signal<boolean>(false);
    private divergedFileSkip = signal<number>(0);
    private divergedFileQuery?: FileQueryCriteria;
    private divergedQueryToken = 0;
    // The file nodes actually visible right now — mirrors the shared (Grid/Mobile) files until
    // diverged, so the common case (nobody has touched a column header yet) shares the same array.
    // "Select all files" and its infinite-scroll growth sweep (below) must key off THIS, not raw
    // `nodes()` directly — otherwise, once diverged, newly-loaded pages land in `divergedFileNodes`
    // and select-all silently stops growing past whatever was loaded at the moment of divergence
    // (DEV-2572).
    public activeFileNodes = computed<DocumentNodeModel[]>(() =>
        this.isFileSortDiverged() ? this.divergedFileNodes() : this.nodes().filter(n => n.type === DocumentNodeType.File)
    );
    public listFileItems = computed<{ node: DocumentNodeModel }[]>(() =>
        this.activeFileNodes().map(node => ({ node }))
    );
    public listHasMoreFiles = computed<boolean>(() =>
        this.isFileSortDiverged() ? this.divergedHasMoreFiles() : this.hasMoreFiles()
    );
    public listIsLoadingMoreFiles = computed<boolean>(() =>
        this.isFileSortDiverged() ? this.isLoadingMoreDivergedFiles() : this.isLoadingMoreFiles()
    );
    public searchTerm = signal<string>('');
    public effectiveSearchTerm = signal<string>('');
    public filter = signal<DocumentViewFilter>({});
    public isLoading = signal<boolean>(true);

    // Browse = navigate folders; Search = global find by name; Activity = the Document Center audit
    // log. Facets refine the browse/search views; Activity replaces the grid entirely.
    public viewMode = signal<DocumentViewMode>(DocumentViewMode.Browse);

    // Desktop/tablet row/column layout for the listing below (DEV-2559) — independent of viewMode.
    // Mobile ignores this entirely (its single card layout is unaffected).
    public viewLayout = signal<DocumentViewLayout>(DocumentViewLayout.List);

    public fileSkip = signal<number>(0);
    public hasMoreFiles = signal<boolean>(false);
    public isLoadingMoreFiles = signal<boolean>(false);

    // Active file query for load-more; queryToken invalidates an in-flight page when the view changes.
    private fileQuery?: FileQueryCriteria;
    private queryToken = 0;

    public isDragging = signal<boolean>(false);
    private dragDepth = 0;

    public permissionsTargets = signal<FolderModel[]>([]);
    public moveTarget = signal<DocumentNodeModel | undefined>(undefined);
    public moveFileTargets = signal<DocumentNodeModel[]>([]);
    public pendingDeleteTarget = signal<DocumentNodeModel | undefined>(undefined);
    public isDeleteSubmitting = signal<boolean>(false);
    public isBulkDeleteFoldersSubmitting = signal<boolean>(false);
    public isBulkDeleteFilesSubmitting = signal<boolean>(false);
    public isEmptyTrashSubmitting = signal<boolean>(false);
    public downloadingId = signal<number | undefined>(undefined);

    public currentFolder = signal<FolderModel | undefined>(undefined);

    // The current folder's parent, used to name the "Back to {parent}" control. Undefined when the
    // current folder sits directly under the root (its parent IS the root, which has no folder record).
    public parentFolder = signal<FolderModel | undefined>(undefined);

    // Label for the "Back" control's destination: "Root" when the parent is the root, otherwise the
    // parent folder's name. The id guard means a not-yet-cleared parent from the folder we just left
    // can never mislabel the button — until the matching parent resolves (or if its fetch fails) we
    // show a generic fallback rather than a stale or misleading "Root".
    public parentLabel = computed<string>(() => {
        const current = this.currentFolder();
        if (!current || current.parentFolderId == null) {
            return DocumentsMessagingConstants.DOCUMENTS_ROOT_LABEL();
        }
        const parent = this.parentFolder();
        return parent?.folderId === current.parentFolderId
            ? parent.name
            : DocumentsMessagingConstants.DOCUMENTS_PARENT_FALLBACK_LABEL();
    });

    // A dedicated "Root" control only earns its place when the parent isn't already the root —
    // otherwise "Back to Root" and the root button would do the exact same thing.
    public showRootButton = computed<boolean>(() => this.currentFolder()?.parentFolderId != null);

    // Org-scoped Document Center storage totals driving the usage bar graph. Re-fetched on every
    // folder/file mutation (version bump) and on org switch so the bar stays in step with the grid.
    public storageUsage = signal<DocumentStorageUsageModel | undefined>(undefined);
    public isStorageUsageLoading = signal<boolean>(true);

    // The usage bar graph is gated on documents.manage (same gate the API enforces) — System Admin,
    // Staff, Org Owner, Org Admin. Users without it never see the bar.
    public canViewStorageUsage = computed<boolean>(() => {
        const state = this.authorizationStateService.authorizationState();
        if (!state) return false;
        const organizationId = this.userSessionService.activeOrganization()?.organizationId;
        return this.authorizationService.hasPermission(state, DocumentsPermission.Manage, organizationId);
    });

    public currentFolderPermission = computed<DocumentPermission | undefined>(() =>
        this.currentFolder()?.permission
    );

    public currentOrganizationId = computed<number>(() =>
        this.userSessionService.activeOrganization()?.organizationId ?? 1
    );

    // Upload requires both a destination folder (never at the root) AND the documents.upload
    // permission. Gates the toolbar action and the drag-and-drop overlay, so a member without the
    // permission can't initiate an upload by dropping a file into a folder.
    public canUpload = computed<boolean>(() =>
        this.currentFolderId() != null && this.hasUploadPermission()
    );

    private hasUploadPermission = computed<boolean>(() => {
        const state = this.authorizationStateService.authorizationState();
        if (!state) return false;
        const organizationId = this.userSessionService.activeOrganization()?.organizationId;
        return this.authorizationService.hasPermission(state, DocumentsPermission.Upload, organizationId);
    });

    public uploadProgress = computed<DocumentUploadProgressModel[]>(() =>
        this.uploadManager.items().map(i => ({
            tempId: i.tempId,
            fileName: i.fileName,
            sizeBytes: i.sizeBytes,
            status: i.status,
            percent: i.percent,
            errorMessage: i.errorMessage,
            retryable: i.retryable
        }))
    );

    // Successful commits buffered since the last settled batch, flushed into one summary modal.
    private completedUploads: UploadCompletedEvent[] = [];

    public infoTitle = signal<string>('');
    public infoMessage = signal<string>('');
    public infoModalType = signal<ModalTypes>(ModalTypes.Success);

    // Bulk "Change permissions" applies only to top-level folders, so it counts those, not the raw selection.
    public selectedTopLevelFolderCount = computed<number>(() =>
        this.selectedNodes().filter(n => DocumentUtilities.isTopLevelFolder(n)).length
    );

    // Bulk "Move to…" applies only to selected files (bulk folder move is out of scope), and never to
    // a file that isn't Available — those expose no checkbox, but guard here too in case one was
    // selected while Available and then flipped to Pending/Quarantined on a refresh.
    public selectedFileNodes = computed<DocumentNodeModel[]>(() =>
        this.selectedNodes().filter(n => n.type === DocumentNodeType.File && DocumentUtilities.isFileAvailable(n))
    );

    public selectedFileCount = computed<number>(() => this.selectedFileNodes().length);

    // Bulk delete folders (DEV-2481): unlike bulk "Change permissions", this applies to a folder at any
    // level — the backend cascades each selected folder's own subtree regardless of nesting.
    public selectedFolderNodes = computed<DocumentNodeModel[]>(() =>
        this.selectedNodes().filter(n => n.type === DocumentNodeType.Folder)
    );

    public selectedFolderCount = computed<number>(() => this.selectedFolderNodes().length);

    // Bulk delete files (DEV-2481): unlike bulk Move, this applies regardless of scan status — a
    // Pending/Quarantined file can already be deleted individually via the row kebab.
    public selectedDeleteEligibleFileNodes = computed<DocumentNodeModel[]>(() =>
        this.selectedNodes().filter(n => n.type === DocumentNodeType.File)
    );

    // While "select all files" is active the true count is "everything matching the filter minus what
    // was deselected" — not just what's been scrolled into view.
    public deleteEligibleFileCount = computed<number>(() =>
        this.isAllFilesSelected()
            ? Math.max(0, this.filesTotalCount() - this.deselectedFileIds().size)
            : this.selectedDeleteEligibleFileNodes().length
    );

    // Checked state for the "select all folders" header checkbox — folders are always eager-loaded in
    // full, so this is complete (not just "all loaded so far"). Locked folders have no checkbox and are
    // never selectable, so they're excluded from the sweep.
    public allFoldersSelected = computed<boolean>(() => {
        const folderIds = this.nodes().filter(n => n.type === DocumentNodeType.Folder && !n.isLocked).map(n => n.documentNodeId);
        return folderIds.length > 0 && folderIds.every(id => this.selectedFolderIds().has(id));
    });

    public isSearchMode = computed<boolean>(() => this.viewMode() === DocumentViewMode.Search);

    public isActivityMode = computed<boolean>(() => this.viewMode() === DocumentViewMode.Activity);

    // The Document Center audit log is gated on documents.manage; the toggle is only rendered for
    // users who hold it (the API enforces the same gate server-side).
    public canViewActivityLog = computed<boolean>(() => {
        const state = this.authorizationStateService.authorizationState();
        if (!state) return false;
        const organizationId = this.userSessionService.activeOrganization()?.organizationId;
        return this.authorizationService.hasPermission(state, DocumentsPermission.Manage, organizationId);
    });

    // File-only facets (type/date) apply only where files can exist: search, or inside a folder (not root).
    public canFilterFiles = computed<boolean>(() => this.isSearchMode() || this.currentFolderId() != null);

    // Permissions vary only at the root or in search; inside a folder everything inherits one permission.
    public canFilterByAccess = computed<boolean>(() => this.isSearchMode() || this.currentFolderId() == null);

    // Nothing to refine/sort in Search until a term is entered, and nothing to refine in Activity.
    public showToolbar = computed<boolean>(() =>
        !this.isActivityMode() && (!this.isSearchMode() || this.effectiveSearchTerm().trim().length > 0));

    public currentFolderName = computed<string>(() => this.currentFolder()?.name ?? DocumentsMessagingConstants.DOCUMENTS_ROOT_LABEL());

    public filterScopeLabel = computed<string>(() => {
        if (this.isSearchMode()) return DocumentsMessagingConstants.DOCUMENTS_FILTER_RESULTS_LABEL();
        if (this.currentFolderId() == null) return DocumentsMessagingConstants.DOCUMENTS_FILTER_ROOT_LABEL();
        return DocumentsMessagingConstants.DOCUMENTS_FILTER_FOLDER_LABEL().replaceValuesWithObject({ name: this.currentFolderName() });
    });

    public displayItems = computed<DocumentListItem[]>(() =>
        this.nodes().map(node => ({ node }))
    );

    // A file sits in Pending while its virus scan runs, then flips to Available/Quarantined server-side
    // with no push to the client. When the user returns to the tab we re-fetch — but only if a Pending
    // file is actually on screen, so we don't bust the cache on every tab focus for nothing.
    private hasPendingFiles = computed<boolean>(() =>
        this.activeFileNodes().some(n => n.status === DocumentStatus.Pending)
    );

    // Drives the empty-state copy ("no search results" vs "empty folder").
    public showBreadcrumbColumn = computed<boolean>(() => this.isSearchMode());

    public deleteConfirmMessage = computed<string>(() => {
        const target = this.pendingDeleteTarget();
        if (!target) return '';
        if (target.type === DocumentNodeType.Folder) {
            return DocumentsMessagingConstants.DOCUMENTS_DELETE_FOLDER_CONFIRMATION().replaceValuesWithObject({
                name: target.name,
                itemCount: target.childCount ?? 0
            });
        }
        return DocumentsMessagingConstants.DOCUMENTS_DELETE_FILE_CONFIRMATION().replaceValuesWithObject({ name: target.name });
    });

    // A file delete is a soft-delete to Trash (recoverable), so it uses Trash-framed copy; folders
    // keep the plain Delete framing (handled in their own ticket).
    private isFileDelete = computed<boolean>(() =>
        this.pendingDeleteTarget()?.type === DocumentNodeType.File
    );

    // Drives both the confirmation modal title and its confirm button text.
    public deleteActionLabel = computed<string>(() =>
        this.isFileDelete()
            ? DocumentsMessagingConstants.DOCUMENTS_MOVE_TO_TRASH_TITLE()
            : DocumentsMessagingConstants.DOCUMENTS_DELETE_TITLE()
    );

    public bulkDeleteFoldersTitle = computed<string>(() =>
        DocumentsMessagingConstants.DOCUMENTS_BULK_DELETE_FOLDERS_TITLE().replaceValuesWithObject({ count: this.selectedFolderCount() })
    );

    public bulkDeleteFilesTitle = computed<string>(() =>
        DocumentsMessagingConstants.DOCUMENTS_BULK_DELETE_FILES_TITLE().replaceValuesWithObject({ count: this.deleteEligibleFileCount() })
    );

    public DocumentsMessagingConstants = DocumentsMessagingConstants;
    public DocumentUtilities = DocumentUtilities;
    public AuditEntityType = AuditEntityType;
    public DocumentViewMode = DocumentViewMode;
    public DocumentViewLayout = DocumentViewLayout;

    public readonly permsModalId = DocumentsConstants.PERMISSIONS_MODAL_ID();
    public readonly moveModalId = DocumentsConstants.MOVE_FOLDER_MODAL_ID();
    public readonly bulkMoveModalId = DocumentsConstants.BULK_MOVE_FILES_MODAL_ID();
    public readonly deleteModalId = DocumentsConstants.DELETE_MODAL_ID();
    public readonly bulkDeleteFoldersModalId = DocumentsConstants.BULK_DELETE_FOLDERS_MODAL_ID();
    public readonly bulkDeleteFilesModalId = DocumentsConstants.BULK_DELETE_FILES_MODAL_ID();
    public readonly emptyTrashModalId = DocumentsConstants.EMPTY_TRASH_MODAL_ID();
    public readonly leaveConfirmModalId = DocumentsConstants.LEAVE_UPLOAD_MODAL_ID();
    public readonly infoModalId = DocumentsConstants.INFO_MODAL_ID();

    // Resolver for the in-flight canDeactivate() promise, settled by the leave-confirm modal outputs.
    private pendingLeaveResolve?: (canLeave: boolean) => void;

    // Captured once at construction, not reactively: route guards guarantee the active user is already
    // loaded before this page mounts, and this app's session model never swaps users without a full
    // reload — matching how the sort preference below is also read once, synchronously.
    private viewLayoutStorageKey?: string;

    @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;
    @ViewChild('renameModalRef') renameModalRef?: DocumentRenameModalComponent;
    @ViewChild('moveModalRef') moveModalRef?: DocumentMoveFolderModalComponent;
    @ViewChild('bulkMoveModalRef') bulkMoveModalRef?: DocumentBulkMoveFilesModalComponent;
    @ViewChild('permsModalRef') permsModalRef?: DocumentPermissionsModalComponent;
    @ViewChild('deleteConfirmRef') deleteConfirmRef?: ConfirmationModalComponent;
    @ViewChild('infoModalRef') infoModalRef?: InfoModalComponent;

    constructor() {
        this.sort.set(this.readSortFromStorage());
        this.folderSortOverride.set(this.readSortOverrideFromStorage(LocalStorageKeyConstants.DOCUMENTS_FOLDER_ORDERBY()));
        this.fileSortOverride.set(this.readSortOverrideFromStorage(LocalStorageKeyConstants.DOCUMENTS_FILE_ORDERBY()));

        const userId = this.userSessionService.activeUser()?.userId;
        this.viewLayoutStorageKey = userId != null ? LocalStorageKeyConstants.DOCUMENTS_VIEW_LAYOUT(userId) : undefined;
        this.viewLayout.set(this.readViewLayoutFromStorage());

        this.breakpointObserver.observe('(max-width: 991.98px)')
            .pipe(takeUntilDestroyed())
            .subscribe(state => this.isMobile.set(state.matches));

        effect(() => {
            const order = this.sort();
            this.persistSort(order);
        });

        effect(() => {
            this.persistSortOverride(LocalStorageKeyConstants.DOCUMENTS_FOLDER_ORDERBY(), this.folderSortOverride());
        });

        effect(() => {
            this.persistSortOverride(LocalStorageKeyConstants.DOCUMENTS_FILE_ORDERBY(), this.fileSortOverride());
        });

        effect(() => {
            const layout = this.viewLayout();
            this.persistViewLayout(layout);
        });

        // Size/Type only sort files. When the view can't list files (e.g. browsing the root), clamp a
        // lingering Size/Type sort back to Name so the dropdown selection matches the folder API order.
        effect(() => {
            if (this.canFilterFiles()) return;
            const clamped = this.clampSortToFolderContext(this.sort());
            if (clamped !== this.sort()) {
                this.sort.set(clamped);
            }
        });

        effect(() => {
            // Re-fetch whenever inputs change. Read all dependencies once so
            // Angular tracks them; the data load itself is fire-and-forget.
            const mode = this.viewMode();
            // Activity mode shows the audit log, which loads its own data — skip the document query.
            if (mode === DocumentViewMode.Activity) return;
            const folderId = this.currentFolderId();
            const sort = this.sort();
            const term = this.effectiveSearchTerm();
            const filter = this.filter();
            const _version = this.documentService.version();
            void this.refreshList(mode, folderId, sort, term, filter);
        });

        // Only fires once List view's file sort has actually diverged from the toolbar's baseline
        // (see fileSort/isFileSortDiverged above) — the undiverged case is already covered by the
        // effect above, so this never duplicates that fetch (DEV-2572).
        effect(() => {
            if (!this.isFileSortDiverged()) return;
            const mode = this.viewMode();
            if (mode === DocumentViewMode.Activity) return;
            const folderId = this.currentFolderId();
            const term = this.effectiveSearchTerm();
            const filterVal = this.filter();
            const fileSort = this.fileSort();
            const _version = this.documentService.version();
            void this.refreshDivergedFiles(mode, folderId, fileSort, term, filterVal);
        });

        effect(() => {
            const _version = this.documentService.version();
            void this.refreshFolderTree();
        });

        effect(() => {
            const folderId = this.currentFolderId();
            // Re-fetch the current folder when version ticks too so renames/permission changes
            // refresh the header.
            const _version = this.documentService.version();
            void this.refreshCurrentFolder(folderId);
        });

        // While "select all files" is active, sweep newly-loaded file pages (infinite scroll) into
        // `selectedFileIds` too — minus anything the user explicitly deselected — so select-all keeps
        // covering the list as it grows instead of freezing at whatever was loaded at click time.
        // Reads activeFileNodes() (not raw nodes()) so this keeps growing correctly even once List
        // view's file sort has diverged onto its own independently-paginated fetch (DEV-2572).
        effect(() => {
            if (!this.isAllFilesSelected()) return;
            const excluded = this.deselectedFileIds();
            const fileIds = this.activeFileNodes().map(n => n.documentNodeId);
            this.selectedFileIds.update(set => {
                let changed = false;
                const next = new Set(set);
                for (const id of fileIds) {
                    if (!excluded.has(id) && !next.has(id)) {
                        next.add(id);
                        changed = true;
                    }
                }
                return changed ? next : set;
            });
        });

        effect(() => {
            // Re-fetch storage totals on org switch and on every mutation (version bump). Reading
            // canViewStorageUsage() also tracks the authorization state, so the bar populates once
            // permissions resolve. Without the permission, hold no data (the bar isn't rendered).
            const organizationId = this.userSessionService.activeOrganization()?.organizationId;
            const _version = this.documentService.version();
            if (!organizationId || !this.canViewStorageUsage()) {
                this.storageUsage.set(undefined);
                return;
            }
            void this.refreshStorageUsage(organizationId);
        });

        // Collect each successful commit (the committed files appear via the version-bump refresh) and
        // summarize them in one modal once the whole batch settles — see the hasActiveUploads effect
        // below — so a bulk upload reports "N files uploaded" rather than a modal per file.
        this.uploadManager.completed
            .pipe(takeUntilDestroyed())
            .subscribe(event => this.completedUploads.push(event));

        effect(() => {
            // Wait until nothing is queued or uploading, then report the batch's successes once.
            if (this.uploadManager.hasActiveUploads() || this.completedUploads.length === 0) return;
            const settled = this.completedUploads;
            this.completedUploads = [];
            this.onUploadsSettled(settled);
        });

        // A batch-level rejection (e.g. the batch would exceed the storage limit) surfaces up front,
        // before any file uploads — the per-file rows in the dock also carry the same reason.
        this.uploadManager.batchError
            .pipe(takeUntilDestroyed())
            .subscribe(message => this.showInfo(DocumentsMessagingConstants.DOCUMENTS_UPLOAD_FAILED_TITLE(), message, ModalTypes.Failure));

        // Poll the scan status of any visible Pending file so it flips to Available/Quarantined on its
        // own once the scan clears server-side (centralized in DocumentStatusUtilities). Cheap no-op
        // when nothing is Pending, so it runs for the page's lifetime.
        DocumentStatusUtilities.pollResolvedStatuses(this.documentService, () => this.pendingFileIds())
            .pipe(takeUntilDestroyed())
            .subscribe(resolved => this.applyResolvedStatuses(resolved));
    }

    /** Go up one level: to the parent folder, or to the root when the parent IS the root. */
    public goToParent(): void {
        this.onFolderSelected(this.currentFolder()?.parentFolderId);
    }

    /** Jump straight to the Document Center root from any nested folder — no confirmation. */
    public goToRoot(): void {
        this.onFolderSelected(undefined);
    }

    private async refreshCurrentFolder(folderId: number | undefined): Promise<void> {
        if (!folderId) {
            this.currentFolder.set(undefined);
            this.parentFolder.set(undefined);
            return;
        }
        const result = await this.documentService.getFolderById(folderId);
        if (result.success && result.data) {
            this.currentFolder.set(result.data);
            await this.refreshParentFolder(result.data);
        }
    }

    // Fetch the current folder's parent so the "Back to {parent}" control can name its destination.
    // The parent is fetched by id (cheap via the GET cache, and correct on a deep-link/refresh). When
    // the parent is the root there is no folder record to fetch — the label falls back to "Root".
    private async refreshParentFolder(folder: FolderModel): Promise<void> {
        const parentFolderId = folder.parentFolderId;
        // Drop any previously-held parent that doesn't match the new folder's parent so a stale name
        // can't show while the new parent loads (or if its fetch fails).
        if (this.parentFolder()?.folderId !== parentFolderId) {
            this.parentFolder.set(undefined);
        }
        if (parentFolderId == null) {
            return;
        }
        const result = await this.documentService.getFolderById(parentFolderId);
        // A newer navigation may have superseded this fetch mid-flight — only publish if still current.
        if (result.success && result.data && this.currentFolderId() === folder.folderId) {
            this.parentFolder.set(result.data);
        }
    }

    public ngOnInit(): void {
        // Initial loads — effects will also fire but explicit calls ensure first paint shows data quickly.
        void this.refreshFolderTree();
        void this.refreshList(DocumentViewMode.Browse, undefined, this.sort(), '', {});
    }

    public ngOnDestroy(): void {
        // Dispose the leave-confirm modal instance so Bootstrap releases its listeners; the shared
        // sweep then removes any backdrop the page leaves behind on a mid-modal navigation.
        this.bootstrapService.manageModal(`#${this.leaveConfirmModalId}`, ModalActions.Dispose);
        this.bootstrapService.cleanupOrphanedModals();
    }

    //#region List actions

    public onFolderSelected(folderId: number | null | undefined): void {
        // Normalize null → undefined (a root folder's parentFolderId is null at runtime).
        this.currentFolderId.set(folderId ?? undefined);
        this.clearSelection();
        // Opening a folder leaves Search mode and starts the folder unfiltered.
        this.viewMode.set(DocumentViewMode.Browse);
        this.searchTerm.set('');
        this.effectiveSearchTerm.set('');
        this.filter.set({});
    }

    /** Switch to Search mode: clear any browse facets/term and focus the input once it renders. */
    public enterSearchMode(): void {
        if (this.viewMode() === DocumentViewMode.Search) {
            this.searchInputRef?.nativeElement.focus();
            return;
        }
        this.viewMode.set(DocumentViewMode.Search);
        this.searchTerm.set('');
        this.effectiveSearchTerm.set('');
        this.filter.set({});
        this.clearSelection();
        afterNextRender(() => this.searchInputRef?.nativeElement.focus(), { injector: this.injector });
    }

    /** Return to Browse mode (the folder the user was in) and drop the search term + result facets. */
    public setBrowseMode(): void {
        if (this.viewMode() === DocumentViewMode.Browse) return;
        this.viewMode.set(DocumentViewMode.Browse);
        this.searchTerm.set('');
        this.effectiveSearchTerm.set('');
        this.filter.set({});
        this.clearSelection();
    }

    // Blurring after the click (rather than leaving the button focused) matters because the tooltip
    // directive uses trigger: 'hover focus' for keyboard-tab accessibility — a mouse click can leave
    // the button focused in some browsers, which would keep its tooltip stuck open after the mouse
    // moves away since the focus trigger alone is still satisfied.
    public setViewLayout(layout: DocumentViewLayout, event: MouseEvent): void {
        this.viewLayout.set(layout);
        (event.currentTarget as HTMLElement | null)?.blur();
    }

    /**
     * Toolbar sort dropdown (DEV-2572) — the only sort control in Grid/Mobile, and still visible in
     * List view. Resets both folder/file overrides back to "mirror the toolbar," so it stays a
     * meaningful "sort both the same way" action instead of becoming inert once either table's own
     * header has diverged from it.
     */
    public onGlobalSortChanged(value: DocumentListSortOptions): void {
        this.sort.set(value);
        this.folderSortOverride.set(undefined);
        this.fileSortOverride.set(undefined);
    }

    /** List view's folder table header (DEV-2572) — purely a client-side re-order, no fetch involved. */
    public onFolderSortChanged(value: DocumentListSortOptions): void {
        this.folderSortOverride.set(value);
    }

    /**
     * List view's file table header (DEV-2572). The table shows skeleton rows (isFilesRefreshing) while
     * the newly-sorted page loads, so there's no need to pre-seed divergedFileNodes from the old data —
     * it's simply not rendered during that window.
     */
    public onFileSortChanged(value: DocumentListSortOptions): void {
        this.fileSortOverride.set(value);
    }

    /** Show the Document Center audit log: drop the browse/search term, facets, and selection. */
    public enterActivityMode(): void {
        if (this.viewMode() === DocumentViewMode.Activity) return;
        this.viewMode.set(DocumentViewMode.Activity);
        this.searchTerm.set('');
        this.effectiveSearchTerm.set('');
        this.filter.set({});
        this.clearSelection();
    }

    public onSearch(): void {
        this.effectiveSearchTerm.set(this.searchTerm().trim());
    }

    public clearSearch(): void {
        this.searchTerm.set('');
        this.effectiveSearchTerm.set('');
    }

    public onFolderOpened(node: DocumentNodeModel): void {
        this.onFolderSelected(node.documentNodeId);
    }

    // Clicking a file downloads it in the current tab: resolve a short-lived URL from the API, fetch
    // the bytes from Azure (interceptor-free, so no JWT goes to Azure), then save via an object URL.
    // This never changes the URL or adds an attachment disposition, so the same plain public CDN URL
    // stays usable as an <img src> / preview. Reading the bytes requires the public CDN to send the
    // CORS allow-header. Failures stay metadata-free.
    public async onDownloadRequested(node: DocumentNodeModel): Promise<void> {
        // Defence in depth: the list already blocks clicks and hides the Download action for files
        // that aren't Available, but guard here too so a Pending/Quarantined file can never be
        // downloaded — and explain why if one is somehow reached.
        if (!DocumentUtilities.isFileAvailable(node)) {
            const message = node.status === DocumentStatus.Quarantined
                ? DocumentsMessagingConstants.DOCUMENTS_DOWNLOAD_QUARANTINED()
                : DocumentsMessagingConstants.DOCUMENTS_DOWNLOAD_PENDING();
            this.showInfo(DocumentsMessagingConstants.DOCUMENTS_DOWNLOAD_UNAVAILABLE_TITLE(), message, ModalTypes.Failure);
            return;
        }
        // One download at a time: ignore any click while a download is already in flight.
        if (this.downloadingId() != null) return;
        this.downloadingId.set(node.documentNodeId);
        try {
            const result = await this.documentService.getDownloadUrl(node.documentNodeId);
            if (!result.success || !result.data) {
                this.showInfo(DocumentsMessagingConstants.DOCUMENTS_DOWNLOAD_UNAVAILABLE_TITLE(),
                    this.downloadFailureMessage(result.statusCode), ModalTypes.Failure);
                return;
            }

            const fileResult = await this.documentService.fetchDocumentBlob(result.data.downloadUrl);
            if (fileResult.success && fileResult.data) {
                FileDownloadUtilities.downloadBlob(fileResult.data, result.data.name);
            } else {
                this.showInfo(DocumentsMessagingConstants.DOCUMENTS_DOWNLOAD_UNAVAILABLE_TITLE(),
                    DocumentsMessagingConstants.DOCUMENTS_DOWNLOAD_FAILED(), ModalTypes.Failure);
            }
        } finally {
            this.downloadingId.set(undefined);
        }
    }

    // Metadata-free copy for a failed download-URL request: denied (403), gone (404), else generic.
    private downloadFailureMessage(statusCode?: number): string {
        if (statusCode === 403) return DocumentsMessagingConstants.DOCUMENTS_DOWNLOAD_DENIED();
        if (statusCode === 404) return DocumentsMessagingConstants.DOCUMENTS_DOWNLOAD_GONE();
        return DocumentsMessagingConstants.DOCUMENTS_DOWNLOAD_FAILED();
    }

    public onSelectionToggled(event: { documentNodeId: number, type: DocumentNodeType, checked: boolean }): void {
        // While "select all files" is active, an individual file toggle also updates the exclusion set
        // that the bulk-delete-files request's `excludedIds` is built from.
        if (this.isAllFilesSelected() && event.type === DocumentNodeType.File) {
            this.deselectedFileIds.update(set => {
                const next = new Set(set);
                if (event.checked) {
                    next.delete(event.documentNodeId);
                } else {
                    next.add(event.documentNodeId);
                }
                return next;
            });
        }
        // Folders and files never share a set — see selectedFolderIds/selectedFileIds — so an id
        // collision between a folder and a file can never toggle the wrong one.
        const target = event.type === DocumentNodeType.Folder ? this.selectedFolderIds : this.selectedFileIds;
        target.update(set => {
            const next = new Set(set);
            if (event.checked) {
                next.add(event.documentNodeId);
            } else {
                next.delete(event.documentNodeId);
            }
            return next;
        });
    }

    /**
     * "Select all folders" — folders are always eager-loaded in full, so this is a simple id sweep.
     * Locked folders are excluded: they have no checkbox and no bulk action applies to them.
     */
    public onSelectAllFoldersToggled(checked: boolean): void {
        const folderIds = this.nodes().filter(n => n.type === DocumentNodeType.Folder && !n.isLocked).map(n => n.documentNodeId);
        this.selectedFolderIds.update(set => {
            const next = new Set(set);
            for (const id of folderIds) {
                if (checked) next.add(id); else next.delete(id);
            }
            return next;
        });
    }

    /**
     * "Select all files" — true select-all (DEV-2481): sets `isAllFilesSelected` (consumed by the bulk
     * delete request builder) and syncs `selectedFileIds` with what's currently loaded for immediate
     * visual feedback; the constructor's growth effect keeps adding newly-loaded pages while it stays
     * active. Never touches folder selection — see selectedFolderIds/selectedFileIds.
     */
    public onSelectAllFilesToggled(checked: boolean): void {
        this.isAllFilesSelected.set(checked);
        this.deselectedFileIds.set(new Set<number>());
        const fileIds = this.activeFileNodes().map(n => n.documentNodeId);
        this.selectedFileIds.update(set => {
            const next = new Set(set);
            for (const id of fileIds) {
                if (checked) next.add(id); else next.delete(id);
            }
            return next;
        });
    }

    public onRenameRequested(node: DocumentNodeModel): void {
        this.renameModalRef?.openRename(node);
    }

    public onMoveRequested(node: DocumentNodeModel): void {
        // Files have no single-move endpoint, so a single file's "Move to…" reuses the bulk-files move
        // flow as a one-item selection (correct file semantics: folder-only destinations, no Root).
        // Folders use the dedicated folder-move modal.
        if (node.type === DocumentNodeType.File) {
            this.moveFileTargets.set([node]);
            this.openModal(this.bulkMoveModalId);
            return;
        }

        this.moveTarget.set(node);
        this.openModal(this.moveModalId);
    }

    public onMoveSubmitted(result: FolderModel): void {
        this.moveTarget.set(undefined);
        this.clearSelection();
        this.showInfo(DocumentsMessagingConstants.DOCUMENTS_FOLDER_MOVED_TITLE(), DocumentsMessagingConstants.DOCUMENTS_MOVE_SUCCESS().replaceValuesWithObject({ name: result.name }), ModalTypes.Success);
    }

    public onMoveCancelled(): void {
        this.moveTarget.set(undefined);
    }

    public onBulkMoveRequested(): void {
        // Bulk move acts on selected files only (bulk folder move is out of scope).
        const fileTargets = this.selectedFileNodes();
        if (fileTargets.length === 0) return;
        this.moveFileTargets.set(fileTargets);
        this.openModal(this.bulkMoveModalId);
    }

    public onBulkMoveSubmitted(result: BulkActionResultModel): void {
        this.moveFileTargets.set([]);
        this.clearSelection();
        const message = result.totalSkipped > 0
            ? DocumentsMessagingConstants.DOCUMENTS_BULK_MOVE_PARTIAL().replaceValuesWithObject({ count: result.totalAffected, skipped: result.totalSkipped })
            : DocumentsMessagingConstants.DOCUMENTS_BULK_MOVE_SUCCESS().replaceValuesWithObject({ count: result.totalAffected });
        this.showInfo(DocumentsMessagingConstants.DOCUMENTS_BULK_MOVE_TITLE(), message, result.totalSkipped > 0 ? ModalTypes.Information : ModalTypes.Success);
    }

    public onBulkMoveCancelled(): void {
        this.moveFileTargets.set([]);
    }

    public onNewFolderRequested(): void {
        this.renameModalRef?.openCreateFolder(this.currentFolderId(), this.currentFolderPermission(), this.currentOrganizationId());
    }

    public onPermissionsRequested(node: DocumentNodeModel): void {
        // Only top-level folders have an editable permission; descendants inherit it. The kebab only
        // surfaces this action for top-level folders, so this is just a defensive guard (no modal).
        if (!DocumentUtilities.isTopLevelFolder(node)) return;
        this.permissionsTargets.set([this.toFolderModel(node)]);
        this.openModal(this.permsModalId);
    }

    public onBulkPermissionsRequested(): void {
        // The bulk action is only offered when the selection includes top-level folders, and it acts
        // only on those (descendants inherit their top-level folder's permission).
        const folderTargets = this.selectedNodes().filter(n => DocumentUtilities.isTopLevelFolder(n));
        if (folderTargets.length === 0) return;
        this.permissionsTargets.set(folderTargets.map(n => this.toFolderModel(n)));
        this.openModal(this.permsModalId);
    }

    private toFolderModel(node: DocumentNodeModel): FolderModel {
        return {
            folderId: node.documentNodeId,
            organizationId: node.organizationId,
            parentFolderId: node.parentId,
            name: node.name,
            permission: node.permission,
            fileCount: node.childFileCount ?? 0,
            isLocked: node.isLocked ?? false,
            childFolderCount: node.childFolderCount ?? 0,
            isEmpty: node.isEmpty ?? ((node.childCount ?? 0) === 0),
            created: node.createdDate,
            lastModified: node.modifiedDate
        };
    }

    public onDeleteRequested(node: DocumentNodeModel): void {
        this.pendingDeleteTarget.set(node);
        this.openModal(this.deleteModalId);
    }

    public async onDeleteConfirmed(): Promise<void> {
        const target = this.pendingDeleteTarget();
        if (!target) return;
        this.isDeleteSubmitting.set(true);
        try {
            // Same predicate that drives the modal copy/title, so the action always matches the UI.
            const isFile = this.isFileDelete();
            const result = isFile
                ? await this.documentService.deleteDocument(target.documentNodeId)
                : await this.documentService.deleteFolder(target.documentNodeId);
            this.closeModal(this.deleteModalId);
            if (result.success) {
                this.pendingDeleteTarget.set(undefined);
                this.clearSelection();
                const title = isFile
                    ? DocumentsMessagingConstants.DOCUMENTS_FILE_TRASHED_TITLE()
                    : DocumentsMessagingConstants.DOCUMENTS_DELETED_TITLE();
                const message = isFile
                    ? DocumentsMessagingConstants.DOCUMENTS_DELETE_FILE_SUCCESS().replaceValuesWithObject({ name: target.name })
                    : DocumentsMessagingConstants.DOCUMENTS_DELETE_FOLDER_SUCCESS().replaceValuesWithObject({ name: target.name });
                this.showInfo(title, message, ModalTypes.Success);
            } else {
                // Keep the pending target intact so the user can retry.
                this.showInfo(DocumentsMessagingConstants.DOCUMENTS_DELETE_FAILED_TITLE(), result.message ?? DocumentsMessagingConstants.DOCUMENTS_DELETE_FAILED(), ModalTypes.Failure);
            }
        } finally {
            this.isDeleteSubmitting.set(false);
        }
    }

    public onDeleteCancelled(): void {
        this.pendingDeleteTarget.set(undefined);
        this.closeModal(this.deleteModalId);
    }

    /** Open the bulk-delete-folders confirmation (DEV-2481). */
    public onBulkDeleteFoldersRequested(): void {
        if (this.selectedFolderCount() === 0) return;
        this.openModal(this.bulkDeleteFoldersModalId);
    }

    public async onBulkDeleteFoldersConfirmed(): Promise<void> {
        this.isBulkDeleteFoldersSubmitting.set(true);
        try {
            const request: FolderBulkActionRequest = {
                useFilter: false,
                filter: null,
                selectedIds: this.selectedFolderNodes().map(n => n.documentNodeId),
                excludedIds: null,
                note: null
            };
            const result = await this.documentService.bulkDeleteFolders(request);
            this.closeModal(this.bulkDeleteFoldersModalId);
            if (result.success && result.data) {
                this.clearSelection();
                const message = result.data.totalSkipped > 0
                    ? DocumentsMessagingConstants.DOCUMENTS_BULK_DELETE_FOLDERS_PARTIAL().replaceValuesWithObject({ count: result.data.totalAffected, skipped: result.data.totalSkipped })
                    : DocumentsMessagingConstants.DOCUMENTS_BULK_DELETE_FOLDERS_SUCCESS().replaceValuesWithObject({ count: result.data.totalAffected });
                this.showInfo(DocumentsMessagingConstants.DOCUMENTS_BULK_DELETE_FOLDERS_DONE_TITLE(), message, result.data.totalSkipped > 0 ? ModalTypes.Information : ModalTypes.Success);
            } else {
                // Keep the selection intact so the user can retry.
                this.showInfo(DocumentsMessagingConstants.DOCUMENTS_DELETE_FAILED_TITLE(), result.message ?? DocumentsMessagingConstants.DOCUMENTS_BULK_DELETE_FOLDERS_FAILED(), ModalTypes.Failure);
            }
        } finally {
            this.isBulkDeleteFoldersSubmitting.set(false);
        }
    }

    public onBulkDeleteFoldersCancelled(): void {
        this.closeModal(this.bulkDeleteFoldersModalId);
    }

    /** Open the bulk-delete-files confirmation (DEV-2481). */
    public onBulkDeleteFilesRequested(): void {
        if (this.deleteEligibleFileCount() === 0) return;
        this.openModal(this.bulkDeleteFilesModalId);
    }

    public async onBulkDeleteFilesConfirmed(): Promise<void> {
        this.isBulkDeleteFilesSubmitting.set(true);
        try {
            const request: DocumentBulkActionRequest = this.isAllFilesSelected()
                ? {
                    useFilter: true,
                    filter: this.fileQuery ? (this.documentService.buildDocumentFilter(this.fileQuery) ?? null) : null,
                    selectedIds: null,
                    excludedIds: [...this.deselectedFileIds()],
                    note: null
                }
                : {
                    useFilter: false,
                    filter: null,
                    selectedIds: this.selectedDeleteEligibleFileNodes().map(n => n.documentNodeId),
                    excludedIds: null,
                    note: null
                };
            const result = await this.documentService.bulkDeleteDocuments(request);
            this.closeModal(this.bulkDeleteFilesModalId);
            if (result.success && result.data) {
                this.clearSelection();
                const message = result.data.totalSkipped > 0
                    ? DocumentsMessagingConstants.DOCUMENTS_BULK_DELETE_FILES_PARTIAL().replaceValuesWithObject({ count: result.data.totalAffected, skipped: result.data.totalSkipped })
                    : DocumentsMessagingConstants.DOCUMENTS_BULK_DELETE_FILES_SUCCESS().replaceValuesWithObject({ count: result.data.totalAffected });
                this.showInfo(DocumentsMessagingConstants.DOCUMENTS_BULK_DELETE_FILES_DONE_TITLE(), message, result.data.totalSkipped > 0 ? ModalTypes.Information : ModalTypes.Success);
            } else {
                // Keep the selection intact so the user can retry.
                this.showInfo(DocumentsMessagingConstants.DOCUMENTS_DELETE_FAILED_TITLE(), result.message ?? DocumentsMessagingConstants.DOCUMENTS_BULK_DELETE_FILES_FAILED(), ModalTypes.Failure);
            }
        } finally {
            this.isBulkDeleteFilesSubmitting.set(false);
        }
    }

    public onBulkDeleteFilesCancelled(): void {
        this.closeModal(this.bulkDeleteFilesModalId);
    }

    /** Open the Empty Trash confirmation (raised by the usage bar's button). */
    public onClearTrashRequested(): void {
        this.openModal(this.emptyTrashModalId);
    }

    /**
     * Permanently empty the org's Trash. On success the service's version bump re-fetches the usage
     * bar, so Trash drops to zero and Free grows on its own. A zero-affected result is reported as an
     * info (the Trash was already empty). Either way the confirmation modal closes and the outcome is
     * shown in an info modal; on failure the user can retry from the usage bar's Empty Trash button,
     * which remains enabled while the Trash still has files.
     */
    public async onEmptyTrashConfirmed(): Promise<void> {
        const organizationId = this.userSessionService.activeOrganization()?.organizationId;
        if (!organizationId) return;
        this.isEmptyTrashSubmitting.set(true);
        try {
            const result = await this.documentService.emptyTrash(organizationId);
            this.closeModal(this.emptyTrashModalId);
            if (result.success) {
                const affected = result.data?.totalAffected ?? 0;
                const message = affected > 0
                    ? DocumentsMessagingConstants.DOCUMENTS_EMPTY_TRASH_SUCCESS().replaceValuesWithObject({ count: affected })
                    : DocumentsMessagingConstants.DOCUMENTS_EMPTY_TRASH_NONE();
                this.showInfo(DocumentsMessagingConstants.DOCUMENTS_EMPTY_TRASH_DONE_TITLE(), message, affected > 0 ? ModalTypes.Success : ModalTypes.Information);
            } else {
                this.showInfo(DocumentsMessagingConstants.DOCUMENTS_EMPTY_TRASH_FAILED_TITLE(), result.message ?? DocumentsMessagingConstants.DOCUMENTS_EMPTY_TRASH_FAILED(), ModalTypes.Failure);
            }
        } finally {
            this.isEmptyTrashSubmitting.set(false);
        }
    }

    public onEmptyTrashCancelled(): void {
        this.closeModal(this.emptyTrashModalId);
    }

    public onPermissionsSubmitted(event: PermissionUpdateResult): void {
        if (event.failures.length === 0) {
            this.permissionsTargets.set([]);
            this.clearSelection();
            this.showInfo(DocumentsMessagingConstants.DOCUMENTS_PERMISSION_UPDATED_TITLE(), DocumentsMessagingConstants.DOCUMENTS_PERMISSION_UPDATE_SUCCESS().replaceValuesWithObject({ count: event.updated }), ModalTypes.Success);
            return;
        }
        // Partial success or full failure — keep the modal context but refresh the list to reflect any successes.
        if (event.updated > 0) {
            this.clearSelection();
        }
        // Failure details remain visible inside the modal; no info modal needed here.
    }

    public onPermissionsCancelled(): void {
        this.permissionsTargets.set([]);
    }

    public onRenameSubmitted(event: { name: string, mode: RenameMode }): void {
        if (event.mode === RenameMode.CreateFolder) {
            this.showInfo(DocumentsMessagingConstants.DOCUMENTS_FOLDER_CREATED_TITLE(), DocumentsMessagingConstants.DOCUMENTS_CREATE_FOLDER_SUCCESS().replaceValuesWithObject({ name: event.name }), ModalTypes.Success);
        } else {
            this.showInfo(DocumentsMessagingConstants.DOCUMENTS_RENAMED_TITLE(), DocumentsMessagingConstants.DOCUMENTS_RENAME_SUCCESS().replaceValuesWithObject({ name: event.name }), ModalTypes.Success);
        }
    }

    //#endregion List actions

    //#region Upload

    public onUploadFilesRequested(files: File[]): void {
        const folderId = this.currentFolderId();
        if (folderId == null) return;
        this.uploadManager.enqueue(files, folderId);
    }

    public async onLoadMoreFiles(): Promise<void> {
        const query = this.fileQuery;
        if (!query || this.isLoadingMoreFiles() || !this.hasMoreFiles()) return;

        // Discard the page if the view changed (token bumped) while the request was in flight.
        const token = this.queryToken;
        const requestedSkip = this.fileSkip();
        this.isLoadingMoreFiles.set(true);
        try {
            const result = await this.documentService.getFiles(query, this.sort(), requestedSkip, DocumentsConstants.FILES_PAGE_SIZE());
            if (this.queryToken !== token || this.fileSkip() !== requestedSkip) return;
            if (result.success && result.data) {
                const files = result.data.data ?? [];
                this.nodes.update(list => [...list, ...files]);
                this.fileSkip.update(s => s + files.length);
                this.hasMoreFiles.set(result.data.hasNext ?? false);
            }
        } finally {
            this.isLoadingMoreFiles.set(false);
        }
    }

    // Report a finished batch in one modal. A single file keeps its specific (rename-aware) message;
    // multiple files are summarized as a count, noting how many were auto-renamed to avoid collisions.
    private onUploadsSettled(events: UploadCompletedEvent[]): void {
        if (events.length === 1) {
            this.onUploadCompleted(events[0]);
            return;
        }
        const renamed = events.filter(e => e.document.name !== e.originalName).length;
        const message = renamed > 0
            ? DocumentsMessagingConstants.DOCUMENTS_UPLOAD_BATCH_SUCCESS_RENAMED().replaceValuesWithObject({ count: events.length, renamed })
            : DocumentsMessagingConstants.DOCUMENTS_UPLOAD_BATCH_SUCCESS().replaceValuesWithObject({ count: events.length });
        this.showInfo(DocumentsMessagingConstants.DOCUMENTS_UPLOAD_COMPLETE_TITLE(), message, renamed > 0 ? ModalTypes.Information : ModalTypes.Success);
    }

    private onUploadCompleted(event: UploadCompletedEvent): void {
        const wasRenamed = event.document.name !== event.originalName;
        const message = wasRenamed
            ? DocumentsMessagingConstants.DOCUMENTS_UPLOAD_RENAMED().replaceValuesWithObject({ name: event.document.name })
            : DocumentsMessagingConstants.DOCUMENTS_UPLOAD_SUCCESS().replaceValuesWithObject({ name: event.document.name });
        this.showInfo(DocumentsMessagingConstants.DOCUMENTS_UPLOAD_COMPLETE_TITLE(), message, wasRenamed ? ModalTypes.Information : ModalTypes.Success);
    }

    //#endregion Upload

    //#region Drag and drop (single file, only inside a folder)

    // Listeners are window-scoped (not host-scoped) so a file dragged anywhere over the page — not
    // just over the short content card — triggers the full-screen drop overlay. We always
    // preventDefault on a file drag/drop while on this page so the browser never navigates to /
    // opens a dropped file. The overlay + upload only engage inside a folder (canUpload).

    @HostListener('window:dragenter', ['$event'])
    public onDragEnter(event: DragEvent): void {
        if (!this.hasFiles(event)) return;
        event.preventDefault();
        this.dragDepth++;
        if (this.canUpload()) this.isDragging.set(true);
    }

    @HostListener('window:dragover', ['$event'])
    public onDragOver(event: DragEvent): void {
        if (!this.hasFiles(event)) return;
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = this.canUpload() ? 'copy' : 'none';
    }

    @HostListener('window:dragleave', ['$event'])
    public onDragLeave(event: DragEvent): void {
        if (!this.hasFiles(event)) return;
        event.preventDefault();
        this.dragDepth = Math.max(0, this.dragDepth - 1);
        if (this.dragDepth === 0) this.isDragging.set(false);
    }

    @HostListener('window:drop', ['$event'])
    public onDrop(event: DragEvent): void {
        if (!this.hasFiles(event)) return;
        event.preventDefault();
        this.dragDepth = 0;
        this.isDragging.set(false);
        // Needs the upload permission and a destination folder (never the root); canUpload covers both.
        if (!this.canUpload() || !event.dataTransfer) return;
        const folderId = this.currentFolderId();
        if (folderId == null) return;

        // Folder upload isn't supported in v1: surface each dropped folder as a failed dock row and
        // upload nothing else from this drop.
        const directoryNames = this.droppedDirectoryNames(event.dataTransfer);
        if (directoryNames.length > 0) {
            directoryNames.forEach(name => this.uploadManager.rejectFolder(name));
            return;
        }

        const files = Array.from(event.dataTransfer.files);
        if (files.length === 0) return;
        this.uploadManager.enqueue(files, folderId);
    }

    private hasFiles(event: DragEvent): boolean {
        return !!event.dataTransfer && Array.from(event.dataTransfer.types).includes('Files');
    }

    // Names of any directories in the drop. `webkitGetAsEntry()` is the only reliable folder signal —
    // a dropped folder still appears in `dataTransfer.files`, so the file list alone can't tell us.
    private droppedDirectoryNames(dataTransfer: DataTransfer): string[] {
        const items = dataTransfer.items;
        if (!items) return [];
        const names: string[] = [];
        for (let index = 0; index < items.length; index++) {
            const entry = items[index].webkitGetAsEntry?.();
            if (entry?.isDirectory) {
                names.push(entry.name);
            }
        }
        return names;
    }

    //#endregion Drag and drop

    //#region Scan-status freshness

    // Returning to the tab is the natural moment to reconcile scan status: re-fetch (cache-busted) so
    // any file whose scan finished while the user was away flips out of Pending. Gated on a Pending
    // file actually being visible, so an ordinary tab switch doesn't trigger a needless refetch.
    @HostListener('document:visibilitychange')
    public onVisibilityChange(): void {
        if (document.visibilityState !== 'visible') return;
        if (!this.hasPendingFiles()) return;
        this.documentService.refreshNow();
    }

    // Ids of the files currently Pending a scan — the poll input. Reads activeFileNodes() so a
    // diverged List-view fetch's Pending files are still polled, not just the ones in `nodes()`.
    private pendingFileIds(): number[] {
        return this.activeFileNodes()
            .filter(n => n.status === DocumentStatus.Pending)
            .map(n => n.documentNodeId);
    }

    // Flip resolved files in place — re-listing the whole folder would reset pagination and scroll, so
    // we touch only the rows that changed. Match on file nodes only: folder nodes reuse documentNodeId
    // (mapped from folderId), so a folder whose id equals a polled file's id must never inherit its
    // status. Updates both `nodes` (Grid/Mobile) and `divergedFileNodes` (List view once diverged) —
    // a resolved file may only exist in one of the two once they've diverged (DEV-2572).
    private applyResolvedStatuses(resolved: Map<number, DocumentStatus>): void {
        const applyTo = (node: DocumentNodeModel): DocumentNodeModel =>
            node.type === DocumentNodeType.File && resolved.has(node.documentNodeId)
                ? { ...node, status: resolved.get(node.documentNodeId) }
                : node;
        this.nodes.update(list => list.map(applyTo));
        this.divergedFileNodes.update(list => list.map(applyTo));
    }

    //#endregion Scan-status freshness

    //#region Leave-while-uploading guards

    @HostListener('window:beforeunload', ['$event'])
    public onBeforeUnload(event: BeforeUnloadEvent): void {
        if (this.uploadManager.hasActiveUploads()) {
            event.preventDefault();
            event.returnValue = '';
        }
    }

    /**
     * CanDeactivate hook — confirm leaving the documents route while an upload is in flight. Uses
     * our themed confirmation modal (not the native confirm) and returns a promise the router awaits;
     * the modal's confirmed/cancelled outputs resolve it. (Tab close / refresh can't use a custom
     * modal — browsers force their own generic dialog — so that path stays on `beforeunload` above.)
     */
    public canDeactivate(): boolean | Promise<boolean> {
        if (!this.uploadManager.hasActiveUploads()) return true;
        // Re-entrancy guard: if the confirm modal is already open from a prior navigation attempt,
        // reject the new one rather than overwriting (and permanently orphaning) the original promise.
        if (this.pendingLeaveResolve) return false;
        this.openModal(this.leaveConfirmModalId);
        return new Promise<boolean>(resolve => { this.pendingLeaveResolve = resolve; });
    }

    public onLeaveConfirmed(): void {
        this.closeModal(this.leaveConfirmModalId);
        this.resolveLeave(true);
    }

    public onLeaveCancelled(): void {
        this.closeModal(this.leaveConfirmModalId);
        this.resolveLeave(false);
    }

    private resolveLeave(canLeave: boolean): void {
        this.pendingLeaveResolve?.(canLeave);
        this.pendingLeaveResolve = undefined;
    }

    //#endregion Leave guards

    //#region Data refresh

    // Shared by refreshList (Grid/Mobile + List's undiverged path) and refreshDivergedFiles (List's
    // diverged path) so the two never disagree about *what* to query — only *how it's sorted* (DEV-2572).
    private buildFileQuery(mode: DocumentViewMode.Browse | DocumentViewMode.Search, folderId: number | undefined, term: string, filter: DocumentViewFilter): FileQueryCriteria | undefined {
        const permissions = filter.permissions;
        // Search → global (no folderId); Browse → current folder (none at root). Facets refine either.
        return mode === DocumentViewMode.Search
            ? { searchTerm: term, fileTypes: filter.fileTypes, permissions, modifiedAfter: filter.modifiedAfter, modifiedBefore: filter.modifiedBefore }
            : (folderId != null ? { folderId, fileTypes: filter.fileTypes, permissions, modifiedAfter: filter.modifiedAfter, modifiedBefore: filter.modifiedBefore } : undefined);
    }

    private async refreshList(mode: DocumentViewMode.Browse | DocumentViewMode.Search, folderId: number | undefined, sort: DocumentListSortOptions, term: string, filter: DocumentViewFilter): Promise<void> {
        this.fileSkip.set(0);
        this.hasMoreFiles.set(false);
        this.filesTotalCount.set(0);
        const token = ++this.queryToken;
        const permissions = filter.permissions;

        // Search spans folders only with a typed term; without one, show the prompt (no empty query).
        if (mode === DocumentViewMode.Search && term.trim().length === 0) {
            this.fileQuery = undefined;
            this.nodes.set([]);
            this.isLoading.set(false);
            return;
        }

        this.isLoading.set(true);
        const fileQuery = this.buildFileQuery(mode, folderId, term, filter);
        this.fileQuery = fileQuery;
        try {
            const folderResult = mode === DocumentViewMode.Search
                ? await this.documentService.searchFolders(term, permissions, sort)
                : await this.documentService.getChildren(folderId, sort, permissions);
            const folders = folderResult.success ? (folderResult.data ?? []) : [];

            let files: DocumentNodeModel[] = [];
            if (fileQuery) {
                const fileResult = await this.documentService.getFiles(fileQuery, sort, 0, DocumentsConstants.FILES_PAGE_SIZE());
                // A newer refresh superseded this one mid-flight — drop the stale result.
                if (this.queryToken !== token) return;
                if (fileResult.success && fileResult.data) {
                    files = fileResult.data.data ?? [];
                    this.fileSkip.set(files.length);
                    this.hasMoreFiles.set(fileResult.data.hasNext ?? false);
                    this.filesTotalCount.set(fileResult.data.total ?? files.length);
                }
            }

            if (this.queryToken !== token) return;
            this.nodes.set([...folders, ...files]);
        } finally {
            if (this.queryToken === token) this.isLoading.set(false);
        }
    }

    // List view's diverged file re-fetch (DEV-2572) — only runs once fileSort has actually diverged
    // from the toolbar's baseline. Uses its own token, independent of queryToken, so it can never race
    // against (or be raced by) refreshList's combined Grid/Mobile fetch. Never touches folders/nodes.
    private async refreshDivergedFiles(mode: DocumentViewMode.Browse | DocumentViewMode.Search, folderId: number | undefined, fileSort: DocumentListSortOptions, term: string, filter: DocumentViewFilter): Promise<void> {
        this.divergedFileSkip.set(0);
        this.divergedHasMoreFiles.set(false);
        const token = ++this.divergedQueryToken;
        const fileQuery = this.buildFileQuery(mode, folderId, term, filter);
        this.divergedFileQuery = fileQuery;
        if (!fileQuery) {
            this.divergedFileNodes.set([]);
            return;
        }
        this.isFilesRefreshing.set(true);
        try {
            const result = await this.documentService.getFiles(fileQuery, fileSort, 0, DocumentsConstants.FILES_PAGE_SIZE());
            if (this.divergedQueryToken !== token) return;
            if (result.success && result.data) {
                const files = result.data.data ?? [];
                this.divergedFileNodes.set(files);
                this.divergedFileSkip.set(files.length);
                this.divergedHasMoreFiles.set(result.data.hasNext ?? false);
            }
        } finally {
            if (this.divergedQueryToken === token) this.isFilesRefreshing.set(false);
        }
    }

    /** Infinite scroll for List view's file table — dispatches to whichever pagination state is live. */
    public async onLoadMoreListFiles(): Promise<void> {
        if (!this.isFileSortDiverged()) {
            void this.onLoadMoreFiles();
            return;
        }
        const query = this.divergedFileQuery;
        if (!query || this.isLoadingMoreDivergedFiles() || !this.divergedHasMoreFiles()) return;
        const token = this.divergedQueryToken;
        const requestedSkip = this.divergedFileSkip();
        this.isLoadingMoreDivergedFiles.set(true);
        try {
            const result = await this.documentService.getFiles(query, this.fileSort(), requestedSkip, DocumentsConstants.FILES_PAGE_SIZE());
            if (this.divergedQueryToken !== token || this.divergedFileSkip() !== requestedSkip) return;
            if (result.success && result.data) {
                const files = result.data.data ?? [];
                this.divergedFileNodes.update(list => [...list, ...files]);
                this.divergedFileSkip.update(s => s + files.length);
                this.divergedHasMoreFiles.set(result.data.hasNext ?? false);
            }
        } finally {
            this.isLoadingMoreDivergedFiles.set(false);
        }
    }

    private async refreshFolderTree(): Promise<void> {
        const result = await this.documentService.getFolderTree();
        if (result.success) {
            this.folderTree.set(result.data ?? []);
        }
    }

    private async refreshStorageUsage(organizationId: number): Promise<void> {
        // Drop another org's totals immediately when switching orgs, so a slow or failing fetch can't
        // leave the previous org's numbers on screen. A same-org refresh (e.g. after an upload) keeps
        // the current data visible to avoid a skeleton flicker.
        if (this.storageUsage()?.organizationId !== organizationId) {
            this.storageUsage.set(undefined);
        }
        this.isStorageUsageLoading.set(true);
        try {
            const result = await this.documentService.getStorageUsage(organizationId);
            // On failure, clear rather than retain stale totals.
            this.storageUsage.set(result.success && result.data ? result.data : undefined);
        } finally {
            this.isStorageUsageLoading.set(false);
        }
    }


    /** Clear all selection state, including the "select all files" mode (DEV-2481). */
    private clearSelection(): void {
        this.selectedFolderIds.set(new Set<number>());
        this.selectedFileIds.set(new Set<number>());
        this.isAllFilesSelected.set(false);
        this.deselectedFileIds.set(new Set<number>());
    }

    private selectedNodes(): DocumentNodeModel[] {
        const folderIds = this.selectedFolderIds();
        const fileIds = this.selectedFileIds();
        // Folders always come from `nodes()` (never diverged). Files come from `activeFileNodes()`
        // rather than raw `nodes()` — once List view's file sort has diverged, a selected file may only
        // exist in `divergedFileNodes`, and filtering against `nodes()` alone would silently drop it
        // from bulk delete/move counts and requests (DEV-2572).
        const folders = this.nodes().filter(n => n.type === DocumentNodeType.Folder && folderIds.has(n.documentNodeId));
        const files = this.activeFileNodes().filter(n => fileIds.has(n.documentNodeId));
        return [...folders, ...files];
    }

    //#endregion Data refresh

    //#region Persistence

    private readSortFromStorage(): DocumentListSortOptions {
        try {
            const raw = localStorage.getItem(LocalStorageKeyConstants.DOCUMENTS_ORDERBY());
            if (raw && Object.values(DocumentListSortOptions).includes(raw as DocumentListSortOptions)) {
                return raw as DocumentListSortOptions;
            }
        } catch { /* ignore */ }
        return DocumentListSortOptions.NameAsc;
    }

    private persistSort(value: DocumentListSortOptions): void {
        try {
            localStorage.setItem(LocalStorageKeyConstants.DOCUMENTS_ORDERBY(), value);
        } catch { /* ignore */ }
    }

    // List view's independent folder/file sort overrides (DEV-2572) — absence of the key means "not
    // diverged, mirror the toolbar's sort", so an override is only ever written while actually set and
    // removed (not written as a sentinel) once cleared back to undefined.
    private readSortOverrideFromStorage(key: string): DocumentListSortOptions | undefined {
        try {
            const raw = localStorage.getItem(key);
            if (raw && Object.values(DocumentListSortOptions).includes(raw as DocumentListSortOptions)) {
                return raw as DocumentListSortOptions;
            }
        } catch { /* ignore */ }
        return undefined;
    }

    private persistSortOverride(key: string, value: DocumentListSortOptions | undefined): void {
        try {
            if (value == null) localStorage.removeItem(key);
            else localStorage.setItem(key, value);
        } catch { /* ignore */ }
    }

    private readViewLayoutFromStorage(): DocumentViewLayout {
        if (!this.viewLayoutStorageKey) return DocumentViewLayout.List;
        try {
            const raw = localStorage.getItem(this.viewLayoutStorageKey);
            if (raw && Object.values(DocumentViewLayout).includes(raw as DocumentViewLayout)) {
                return raw as DocumentViewLayout;
            }
        } catch { /* ignore */ }
        return DocumentViewLayout.List;
    }

    private persistViewLayout(value: DocumentViewLayout): void {
        if (!this.viewLayoutStorageKey) return;
        try {
            localStorage.setItem(this.viewLayoutStorageKey, value);
        } catch { /* ignore */ }
    }

    // Map a file-only Size/Type sort to its Name equivalent (preserving direction) for folder-only views.
    private clampSortToFolderContext(sort: DocumentListSortOptions): DocumentListSortOptions {
        switch (sort) {
            case DocumentListSortOptions.SizeAsc:
            case DocumentListSortOptions.TypeAsc:
                return DocumentListSortOptions.NameAsc;
            case DocumentListSortOptions.SizeDesc:
            case DocumentListSortOptions.TypeDesc:
                return DocumentListSortOptions.NameDesc;
            default:
                return sort;
        }
    }

    //#endregion Persistence

    //#region Modal helpers

    private openModal(modalId: string): void {
        this.bootstrapService.manageModal(`#${modalId}`, ModalActions.Show);
    }

    private closeModal(modalId: string): void {
        this.bootstrapService.manageModal(`#${modalId}`, ModalActions.Hide);
    }

    private showInfo(title: string, message: string, type: ModalTypes): void {
        this.infoTitle.set(title);
        this.infoMessage.set(message);
        this.infoModalType.set(type);
        this.openModal(this.infoModalId);
    }

    //#endregion Modal helpers
}
